import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Badge, Button, Empty, Pagination, Select, Skeleton, Tag, message } from 'antd';
import { LikeOutlined, MessageOutlined, UserOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchNotifications, readAllNotifications, readNotification } from '@/apis/notifications';
import { getPostErrorMessage } from '@/apis/posts';
import { refreshNotificationSummary, setSummary, validSummary } from '@/store/modules/notifications';
import PullToRefresh from '@/components/pulltorefresh';
import { applicationColors, applicationLabels } from '@/utils/applications';
import { isApplicationNotification, notificationLabels, notificationTarget } from '@/utils/notifications';
import { notificationGroup, notificationTime } from './notificationPresentation';
import './NotificationsPanel.scss';

const NotificationCover = ({ item, onOpen }) => {
  const [failedSource, setFailedSource] = useState(null);
  const source = item.post_cover_image;
  return <button type="button" className="notificationsPanel-cover" aria-label={`查看帖子：${item.post_title}`}
    title={item.post_title} disabled={!notificationTarget(item)} onClick={onOpen}>
    {source && failedSource !== source
      ? <img src={source} alt="" loading="lazy" onError={() => setFailedSource(source)} />
      : <span className="notificationsPanel-cover-text"><span>{item.post_title}</span></span>}
  </button>;
};

const NotificationsPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const count = useSelector(state => state.notifications?.unread_count || 0);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [retry, setRetry] = useState(0);
  const [cursor, setCursor] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [now, setNow] = useState(Date.now);
  const scrollContainerRef = useRef(null);
  const requestVersion = useRef(0);
  const lock = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  const loadNotifications = useCallback(async () => {
    const version = ++requestVersion.current;
    const active = () => alive.current && requestVersion.current === version;
    setLoading(true); setError(''); setCursor(null);
    try {
      const [list, summary] = await Promise.all([
        fetchNotifications({ ...filters, page, pageSize: 10 }),
        dispatch(refreshNotificationSummary()),
      ]);
      if (!Array.isArray(list?.data) || !list.pagination || !validSummary(summary)) throw new Error('通知响应无效');
      if (active()) { setItems(list.data); setPagination(list.pagination); setCursor(summary.latest_id); }
    } catch (err) {
      const text = getPostErrorMessage(err, '通知加载失败，请稍后重试');
      if (active()) setError(text);
      throw new Error(text);
    } finally { if (active()) setLoading(false); }
  }, [dispatch, filters, page]);
  useEffect(() => {
    void loadNotifications().catch(() => {});
    return () => { requestVersion.current += 1; };
  }, [loadNotifications, retry]);
  const markRead = async item => {
    if (lock.current) return;
    lock.current = true; setBusy(item?.id || 'all');
    try {
      if (item) {
        const result = await readNotification(item.id);
        if (result?.id !== item.id || result.is_read !== true) throw new Error('通知已读响应无效');
        if (!alive.current) return;
        setItems(current => current.map(value => value.id === item.id ? result : value));
        await dispatch(refreshNotificationSummary());
      } else {
        // 使用已展示的统计快照，保留点击之后才到达的新通知。
        const result = await readAllNotifications(cursor);
        if (!validSummary(result) || !Number.isInteger(result.updated)) throw new Error('通知已读响应无效');
        if (!alive.current) return;
        dispatch(setSummary({ summary: { unread_count: result.unread_count, has_unread: result.has_unread, latest_id: result.latest_id,
          alert_unread_count: result.alert_unread_count ?? result.unread_count, has_alert_unread: result.has_alert_unread ?? result.has_unread } }));
        message.success('已将当前通知全部标为已读');
      }
      if (alive.current) setRetry(value => value + 1);
    } catch (err) { if (alive.current) message.error(getPostErrorMessage(err, '标记已读失败，请重试')); }
    finally { lock.current = false; if (alive.current) setBusy(null); }
  };
  const changeFilter = (field, value) => { setPage(1); setFilters(current => ({ ...current, [field]: value })); };
  const open = item => {
    const target = notificationTarget(item);
    if (target) navigate(target);
  };
  return <div className="notificationsPanel" ref={scrollContainerRef}>
    <PullToRefresh enabled={isMobile} disabled={loading || busy !== null} onRefresh={loadNotifications}
      scrollContainerRef={scrollContainerRef} successMessage="通知已刷新">
    <div className="notificationsPanel-toolbar">
      <div className="notificationsPanel-filters">
        <Select aria-label="通知类型" value={filters.kind || ''} onChange={value => changeFilter('kind', value || undefined)} options={[{ value: '', label: '全部互动' }, ...Object.entries(notificationLabels).map(([value, label]) => ({ value, label }))]} />
        <Select aria-label="通知阅读状态" value={filters.is_read || ''} onChange={value => changeFilter('is_read', value || undefined)} options={[{ value: '', label: '全部通知' }, { value: 'false', label: '未读' }, { value: 'true', label: '已读' }]} />
      </div>
      <div className="notificationsPanel-actions">{!isMobile && <Button onClick={() => setRetry(value => value + 1)} disabled={busy !== null || loading}>刷新</Button>}<Button onClick={() => markRead()} loading={busy === 'all'} disabled={busy !== null || loading || cursor === null || count === 0}>全部已读</Button></div>
    </div>
    {loading ? <Skeleton active paragraph={{ rows: 5 }} /> : error ? <Alert type="error" showIcon title={error} action={<Button onClick={() => setRetry(value => value + 1)}>重试</Button>} />
      : items.length === 0 ? <Empty description="暂无符合条件的互动通知" /> : <div className="notificationsPanel-list">{items.map((item, index) => {
        const interaction = ['like', 'comment', 'reply'].includes(item.kind);
        const group = notificationGroup(item.created_at, now);
        return <React.Fragment key={item.id}>
        {(index === 0 || group !== notificationGroup(items[index - 1].created_at, now)) && <h3 className="notificationsPanel-group">{group}</h3>}
        <article className={`notificationsPanel-item ${interaction ? 'is-interaction' : ''} ${item.is_read ? '' : 'is-unread'}`}>
        {notificationTarget(item) && <button type="button" className="notificationsPanel-open" aria-label={`查看通知：${item.post_title}`} onClick={() => open(item)} />}
        <Avatar className="notificationsPanel-avatar" size={isMobile ? 44 : 48} src={item.actor?.avatar || undefined} icon={<UserOutlined />} />
        <div className="notificationsPanel-content">
          <div className="notificationsPanel-heading">
            <div className="notificationsPanel-meta"><strong>{item.actor?.nickname || item.actor?.username || '用户'}</strong>{interaction
              ? <span className={`notificationsPanel-interaction notificationsPanel-interaction-${item.kind}`}>{item.kind === 'like' ? <LikeOutlined /> : <MessageOutlined />}{notificationLabels[item.kind]}</span>
              : <Tag className={`notificationsPanel-kind notificationsPanel-kind-${item.kind}`} icon={<MessageOutlined />}>{notificationLabels[item.kind] || '活动通知'}</Tag>}{!item.is_read && <Badge status={item.should_alert === false ? 'default' : 'error'} text="未读" />}</div>
            {!interaction && <time dateTime={item.created_at} title={new Date(item.created_at).toLocaleString('zh-CN')}>{notificationTime(item.created_at, now)}</time>}
          </div>
          {!interaction && <Button type="link" className="notificationsPanel-post" disabled={!notificationTarget(item)} onClick={() => open(item)}>{item.post_title}</Button>}
          {(interaction || isApplicationNotification(item) || !item.is_read) && <div className="notificationsPanel-footer">
            {interaction && <time dateTime={item.created_at} title={new Date(item.created_at).toLocaleString('zh-CN')}>{notificationTime(item.created_at, now)}</time>}
            {isApplicationNotification(item) && <div className="notificationsPanel-status">
              {item.application_id ? <Tag color={applicationColors[item.application_status] || 'default'}>当前申请：{applicationLabels[item.application_status] || '状态待刷新'}</Tag> : <span>申请当前不可用</span>}
            </div>}
            <div className="notificationsPanel-item-actions">
              {isApplicationNotification(item) && Boolean(item.application_id) && <>
                <Button size="small" onClick={() => open(item)}>查看申请</Button>
                {item.conversation_id && <Button size="small" onClick={() => navigate(`/message/${item.conversation_id}`)}>进入帖子群聊</Button>}
              </>}
              {!item.is_read && <Button type="text" size="small" loading={busy === item.id} disabled={busy !== null} onClick={() => markRead(item)}>标为已读</Button>}
            </div>
          </div>}
        </div>
        {interaction && <NotificationCover item={item} onOpen={() => open(item)} />}
      </article></React.Fragment>;
      })}</div>}
    {!loading && !error && pagination?.total > 10 && <Pagination current={page} pageSize={10} total={pagination.total} showSizeChanger={false} onChange={setPage} />}
    </PullToRefresh>
  </div>;
};
export default NotificationsPanel;
